export interface ParsedHttpResponse {
    status: number;
    statusText: string;
    headers: Record<string, string>;
    body: string;
}

/**
 * Parse a raw HTTP/1.1 response string into structured parts.
 * Handles both Content-Length and Transfer-Encoding: chunked responses.
 */
export function parseRawHttpResponse(raw: string): ParsedHttpResponse {
    const headerEndIdx = raw.indexOf('\r\n\r\n');
    if (headerEndIdx === -1) {
        // No header/body separator — treat everything as body
        return { status: 0, statusText: '', headers: {}, body: raw };
    }

    const headerSection = raw.substring(0, headerEndIdx);
    let bodySection = raw.substring(headerEndIdx + 4);

    const lines = headerSection.split('\r\n');
    const statusLine = lines[0] || '';

    // Parse status line: "HTTP/1.1 200 OK"
    const statusMatch = statusLine.match(/^HTTP\/[\d.]+\s+(\d+)\s*(.*)/);
    const status = statusMatch ? parseInt(statusMatch[1], 10) : 0;
    const statusText = statusMatch ? statusMatch[2].trim() : '';

    // Parse headers
    const headers: Record<string, string> = {};
    for (let i = 1; i < lines.length; i++) {
        const colonIdx = lines[i].indexOf(':');
        if (colonIdx > 0) {
            const key = lines[i].substring(0, colonIdx).trim().toLowerCase();
            const value = lines[i].substring(colonIdx + 1).trim();
            headers[key] = value;
        }
    }

    // Decode chunked transfer-encoding if present
    if (headers['transfer-encoding']?.toLowerCase() === 'chunked') {
        bodySection = decodeChunked(bodySection);
    }

    return { status, statusText, headers, body: bodySection };
}

/**
 * Decode an HTTP chunked transfer-encoded body.
 * Format: hex-size\r\n chunk-data\r\n ... 0\r\n\r\n
 */
function decodeChunked(raw: string): string {
    const parts: string[] = [];
    let pos = 0;

    while (pos < raw.length) {
        const lineEnd = raw.indexOf('\r\n', pos);
        if (lineEnd === -1) break;

        const sizeStr = raw.substring(pos, lineEnd).trim();
        // Strip chunk extensions (e.g. ";ext=value")
        const size = parseInt(sizeStr.split(';')[0], 16);
        if (isNaN(size) || size === 0) break;

        const chunkStart = lineEnd + 2;
        const chunkEnd = chunkStart + size;
        if (chunkEnd > raw.length) {
            // Incomplete chunk — take what we have
            parts.push(raw.substring(chunkStart));
            break;
        }

        parts.push(raw.substring(chunkStart, chunkEnd));
        // Skip past chunk data + trailing \r\n
        pos = chunkEnd + 2;
    }

    return parts.join('');
}
